import { Link } from "react-router-dom";
import { ArrowRight, Target } from "@phosphor-icons/react";
import ProductPageShell from "@/components/ProductPageShell";
import WhistleIcon from "@/components/WhistleIcon";
import { trackPublicEvent } from "@/lib/publicAnalytics";
import coachingEditorialDesktop from "@/assets/coaching-editorial-1600.webp";
import coachingEditorialMobile from "@/assets/coaching-editorial-1000.webp";

const source = "product_coaching";
function track(section, ctaId) {
  trackPublicEvent("cta_click", { source, section, cta_id: ctaId });
}

export default function ProductCoachingPage() {
  return (
    <ProductPageShell source={source}>
      <section className="product-feature-hero product-feature-hero--coaching px-5 pb-16 pt-16 sm:px-8 sm:pb-24 sm:pt-28">
        <div className="product-coaching-art pointer-events-none absolute inset-0" aria-hidden="true">
          <picture className="product-coaching-art__picture">
            <source media="(max-width: 600px)" srcSet={coachingEditorialMobile} />
            <img
              className="product-coaching-art__image"
              src={coachingEditorialDesktop}
              alt=""
              decoding="async"
              fetchPriority="high"
            />
          </picture>
        </div>

        <div className="product-feature-hero__layout relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.94fr_1.06fr] lg:items-start lg:gap-16">
          <div className="product-feature-hero__copy">
            <h1 className="product-feature-hero__title landing-hero-title max-w-3xl text-[2.7rem] font-semibold leading-[1.01] tracking-[-0.055em] sm:text-6xl lg:text-7xl">
              <span className="product-feature-hero__title-base block">See what you</span>
              <span className="product-feature-hero__title-accent landing-hero-spectrum landing-hero-promise block">could have said differently—and why.</span>
            </h1>
            <p className="product-feature-hero-subcopy landing-hero-subcopy marketing-lead-copy mt-7 max-w-2xl">
              Callsect does not coach from abstract scorecards. It goes back to the moment, shows what the customer was really signaling, and turns that evidence into a better approach you can reuse.
            </p>
          </div>

          <article className="product-coaching-proof">
            <div className="product-coaching-proof__head">
              <span><WhistleIcon size={20} /> Coaching move</span>
              <strong>Do not outsource the internal sale to your champion.</strong>
            </div>
            <div className="product-coaching-proof__quote">
              <span>CLIENT</span>
              <blockquote>“I know, and I liked the demo. But I do not have a number I can put in front of her.”</blockquote>
            </div>
            <div className="product-coaching-proof__meaning">
              <span>What it means</span>
              <p>Maya is being asked to carry the business case to Finance without a quantified baseline or direct access.</p>
            </div>
            <div className="product-coaching-proof__wording">
              <span>Example wording</span>
              <p>“Before I give you another deck to carry internally, what is Rachel going to need to believe for this to be worth doing this quarter?”</p>
            </div>
          </article>
        </div>

        <div className="product-feature-hero__actions mx-auto flex flex-col gap-3 sm:flex-row">
          <Link to="/login?mode=register" onClick={() => track("hero", "sign_up_free")} className="product-feature-hero-primary landing-signup-primary-cta inline-flex items-center justify-center rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition">
            Sign up free
          </Link>
          <Link to="/demo" onClick={() => track("hero", "sales_demo")} className="product-feature-hero-secondary landing-hero-secondary-cta inline-flex items-center justify-center gap-2 rounded-xl marketing-secondary-action border px-5 py-3.5 font-medium marketing-text-secondary-strong transition">
            See Demo
            <ArrowRight size={17} weight="bold" />
          </Link>
        </div>
      </section>

      <section className="product-feature-section px-5 py-16 sm:px-8 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:items-start lg:gap-16">
          <div className="product-feature-sticky product-feature-sticky--no-kicker">
            <h2>The lesson only works if the interpretation is grounded.</h2>
            <p className="marketing-lead-copy">Callsect reconstructs what happened and what it meant before it suggests a better approach.</p>
          </div>

          <div className="product-meaning-stack">
            <article>
              <span>What actually happened</span>
              <h3>Finance does not yet believe the problem is urgent enough to deserve budget this quarter.</h3>
              <blockquote>“We have a big onboarding project starting in September, so she is asking why this should compete for budget this quarter.”</blockquote>
              <p>The objection is prioritization against another funded initiative, not simple price resistance.</p>
            </article>
            <article>
              <span>What it really means</span>
              <h3>The champion is still engaged, but the decision moved from product fit to proving urgency.</h3>
              <blockquote>“The reaction was positive overall, but Rachel in Finance came back with a pretty basic question: why does this need to happen now?”</blockquote>
              <p>Positive product reaction is not translating into a funded priority.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="product-feature-band px-5 py-14 sm:px-8 sm:py-16">
        <div className="marketing-highlight-band mx-auto flex max-w-6xl flex-col gap-6 rounded-2xl border p-7 sm:p-9 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <Target size={25} weight="duotone" aria-hidden="true" />
            <h2>Bring the lesson into the next conversation.</h2>
          </div>
          <Link to="/product/prepare-next-call" className="product-feature-inline-link">
            See next-call preparation
            <ArrowRight size={17} weight="bold" />
          </Link>
        </div>
      </section>
    </ProductPageShell>
  );
}
